import React from 'react';
import { styles } from './PromoCard.css';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { useTrackingContext } from '../Tracking';
import { trackingDataToDataAttributes } from '../HeroBanner/utils';
import { YouTubePlayer, YouTubePlayerProps } from '../YouTubePlayer/YouTubePlayer';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { Icon, IconName } from '@jbhi-fi/lanyard-ui/components/Icon';
import { shuffleList } from '@jbhi-fi/utils/src/shuffleList/shuffleList';
import { getSentenceCase } from '@jbhi-fi/utils/src/getSentenceCase/getSentenceCase';
import { TermsAndConditionsLink } from '../TermsAndConditions/TermsAndConditionsLink';
import type { TermsAndConditionsData } from '../TermsAndConditions/TermsAndConditionsModal';

export type PromoCardProps = {
  isHidden?: boolean;
  image?: React.ReactNode;
  youTubeProps?: YouTubePlayerProps;
  href: string;
  isContained?: boolean;
  trackingLabel?: string;
  isDraft?: boolean;
  trackingData?: { id: string; isBranded?: boolean };
  cardFooter?: {
    subText: string;
    buttonLabel: string;
    brandLogo?: IconName;
  };
  termsAndConditions?: TermsAndConditionsData;
};

export const PromoCard = ({
  image,
  href,
  isContained,
  trackingLabel,
  isHidden,
  trackingData,
  youTubeProps,
  cardFooter,
  termsAndConditions,
}: PromoCardProps) => {
  const { tracker } = useTrackingContext();

  if (isHidden) return null;

  const iconName = cardFooter?.brandLogo ?? null;

  const handleClick = () => {
    tracker.trackClick({ label: trackingLabel, extraData: { moduleName: 'Promo Card' } });
  };

  const hrefLink = image && !youTubeProps ? href : undefined;

  return (
    <div className={classNames(styles.container, styles.containerVariants({ isContained }))}>
      <a
        className={styles.linkOverlay}
        href={hrefLink}
        onClick={handleClick}
        {...trackingDataToDataAttributes(trackingData)}
        aria-label="Promo Card"
      />
      <div className={styles.innerContainer({ hasFooter: !!cardFooter })}>
        <div className={styles.mediaContainer}>
          {image ? image : null}
          {youTubeProps ? (
            <div className={classNames(styles.youTubeWrapper, styles.youTubeWrapperInteractive)}>
              <YouTubePlayer {...youTubeProps} orientation="Portrait" />
            </div>
          ) : null}

          {termsAndConditions ? (
            <div className={styles.termsLinkContainer({ isWhite: termsAndConditions?.linkColor === 'white' })}>
              <TermsAndConditionsLink termsAndConditions={termsAndConditions} />
            </div>
          ) : null}
        </div>
        {cardFooter ? (
          <div className={styles.cardFooterSection}>
            {iconName ? <Icon name={iconName} className={styles.brandLogo} /> : null}
            <div className={styles.cardFooter}>
              <div className={styles.footerSubText}>{cardFooter.subText}</div>
              <div className={styles.footerButton}>
                <Button
                  type="primary"
                  href={href}
                  size="sm"
                  classes={styles.buttonLabel}
                  data-testid="promo-card-footer-button"
                >
                  {getSentenceCase(cardFooter.buttonLabel)}
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export type PromoCardCarouselProps = {
  cards: Array<PromoCardProps>;
  lockedCardCount?: number;
  promoCardAutoScroll?: boolean;
};

export const PromoCardCarousel = ({ cards, lockedCardCount, promoCardAutoScroll }: PromoCardCarouselProps) => {
  const isMobile = !useBreakpoint('md', 'up');
  const isDesktop = useBreakpoint('lg', 'up');
  const visibleCards = cards.filter(card => !card.isHidden && !card.isDraft);

  const isPreview = window.Jbhifi?.isContentPreview || false;
  const shouldShuffle = lockedCardCount != undefined && !isPreview;
  const promoCards = shouldShuffle ? shuffleList(visibleCards, lockedCardCount) : visibleCards;

  const nonHiddenCardCount = visibleCards.length;

  return (
    <div className={styles.carouselWrapper}>
      <Carousel
        key={`carousel-${visibleCards.length}`}
        variant={promoCardAutoScroll ? 'autoPlayCarousel' : undefined}
        slidesPerView="auto"
        showPagination={promoCardAutoScroll ? true : false}
        showProgressBar={isMobile || !isDesktop || nonHiddenCardCount > 3}
        showArrows={!isMobile}
        fullBleed={isMobile}
        spaceBetween={isDesktop && nonHiddenCardCount <= 3 ? 43 : 16}
        arrowPosition="bottom"
        slideArrowClassName={styles.slideArrow}
        rewind
        progressBarBulletShape={promoCardAutoScroll ? 'dash' : undefined}
        loop={promoCardAutoScroll ? false : undefined}
        centeredSlides={promoCardAutoScroll ? false : undefined}
      >
        {promoCards.map((cardProps, i) => (
          <PromoCard {...cardProps} key={`${cardProps.href}-${i}-${cardProps.youTubeProps?.videoUrl || 'no-video'}`} />
        ))}
      </Carousel>
    </div>
  );
};
